import { IconButton, TableCell, TableRow } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Fragment, useEffect, useState } from "react";
import CompactOrderPreviewTable from "../../Components/CompactOrderPreviewTable";
import { PreviewInstrumentOption } from "../../Components/OrderPreviewInstrument";
import { getStepInstruments } from "../../Requests/stepRequests";
import { getUsers } from "../../Requests/connections";
import { DropdownItem } from "../../Interfaces/dropdownInterfaces";
import { INLegsAddOrderFormValues, NLegSendMode } from "../../Interfaces/nLegsInterfaces";
import { StepOrderSide } from "../../Interfaces/stepInterfaces";
import { addSeperatorToInput, printPriceWithDecimalsForTableCell, stringifyNumbers } from "../../helper";
import { packetErrorHandler } from "../../Utils/requestUtils";
import { getNLegPreviewOptionType, getNLegPreviewSide, getNLegPreviewVolumeMethod } from "../../Utils/nLegPreview";
import { ReactNode } from "react";
import PrioritySequence from "./PrioritySequence";

interface IProps {
  values: INLegsAddOrderFormValues[] | null;
  onRemoveRow?: (userId: number) => void;
  instrumentOptions?: PreviewInstrumentOption[];
  getAccountIds?: (value: INLegsAddOrderFormValues) => number[];
  costColumns?: { title: string; render: (value: INLegsAddOrderFormValues) => ReactNode }[];
  showContractSize?: boolean;
  showVolumeMethod?: boolean;
}

const NLegOrderPreviewTable = ({ values, onRemoveRow, instrumentOptions, getAccountIds, costColumns, showContractSize, showVolumeMethod }: IProps) => {
  const [fetchedOptions, setOptions] = useState<PreviewInstrumentOption[]>([]);
  const [accounts, setAccounts] = useState<DropdownItem[]>([]);
  const options = instrumentOptions ?? fetchedOptions;

  useEffect(() => {
    if (instrumentOptions) return;
    let active = true;
    getStepInstruments()
      .then((resp) => { if (active) setOptions(resp); })
      .catch(packetErrorHandler);
    return () => { active = false; };
  }, [instrumentOptions]);

  useEffect(() => {
    let active = true;
    getUsers().then((resp) => { if (active) setAccounts(resp); }).catch(packetErrorHandler);
    return () => { active = false; };
  }, []);

  const getHeader = () => (
    <>
      <TableCell>اکانت</TableCell>
      <TableCell>نسبت</TableCell>
      {values?.[0]?.uNLegsItems.map((_, i) => (
        <Fragment key={`leg-${i}`}>
          <TableCell
            className={
              values?.[0]?.uNLegsItems[i].orderSide === StepOrderSide.Buy
                ? "!text-green-600"
                : values?.[0]?.uNLegsItems[i].orderSide === StepOrderSide.Sell
                ? "!text-red-700"
                : "!text-gray-600"
            }
          >
            نماد {stringifyNumbers(i + 1)}
          </TableCell>
          <TableCell>جهت<br />{stringifyNumbers(i + 1)}</TableCell>
          <TableCell><span className="whitespace-nowrap">نوع معامله</span><br />{stringifyNumbers(i + 1)}</TableCell>
        </Fragment>
      ))}
      {values?.[0]?.uNLegsItems.map((_, i) => (
        <TableCell
          key={`volume-${i}`}
          className={
            values?.[0]?.uNLegsItems[i].orderSide === StepOrderSide.Buy
              ? "!text-green-600"
              : values?.[0]?.uNLegsItems[i].orderSide === StepOrderSide.Sell
              ? "!text-red-700"
              : "!text-gray-600"
          }
        >
          {showContractSize ? "اندازه قرارداد" : "حجم"} {stringifyNumbers(i + 1)}
        </TableCell>
      ))}
      {showVolumeMethod ? <TableCell>روش تعیین<br />حجم</TableCell> : null}
      {costColumns ? costColumns.map(column => <TableCell key={column.title}>{column.title}</TableCell>)
        : <TableCell>حداکثر بهای<br />تمام شده</TableCell>}
      <TableCell>نوع ارسال</TableCell>
      <TableCell>ترتیب</TableCell>
      {onRemoveRow ? <TableCell /> : null}
    </>
  );

  const getBody = () => (
    <>
      {values?.length ? (
        values.map((value) => (
          <TableRow key={value.userId}>
            <TableCell>
              {(getAccountIds ? getAccountIds(value) : [value.userId]).map(id =>
                <div key={id}>{accounts.find(account => String(account.id) === String(id))?.name || `اکانت ${id ?? "—"}`}</div>
              )}
            </TableCell>

            <TableCell>
              <div className="flex flex-row justify-start items-center">
                {value.uNLegsItems.map((item, i) => (
                  <span key={`ratio-${value.userId}-${i}`} className="flex items-center">
                    <div
                      className={`${
                        item.orderSide === StepOrderSide.Buy
                          ? "text-green-600"
                          : item.orderSide === StepOrderSide.Sell
                          ? "text-red-700"
                          : "text-gray-600"
                      }`}
                    >
                      {addSeperatorToInput(item.ratio, "priceWithDecimal")}
                    </div>
                    {i !== value.uNLegsItems.length - 1 ? <div>-</div> : null}
                  </span>
                ))}
              </div>
            </TableCell>

            {value.uNLegsItems.map((item, i) => (
              <Fragment key={`leg-${value.userId}-${i}`}>
                <TableCell>
                  {item.instrumentName || options.find(option => String(option.id) === String(item.instrumentId))?.name || item.instrumentId}
                </TableCell>
                <TableCell className={item.orderSide === StepOrderSide.Buy ? "!text-green-600" : item.orderSide === StepOrderSide.Sell ? "!text-red-700" : undefined}>
                  {getNLegPreviewSide(item)}
                </TableCell>
                <TableCell dir="ltr">
                  {getNLegPreviewOptionType(item, options.find(option => String(option.id) === String(item.instrumentId))?.type)}
                </TableCell>
              </Fragment>
            ))}

            {value.uNLegsItems.map((item, i) => (
              <TableCell key={`vol-${value.userId}-${i}`}>
                {showContractSize ? printPriceWithDecimalsForTableCell(item.contractSize)
                  : addSeperatorToInput(Math.round(parseInt(item.volume?.toString())), "price") || "-"}
              </TableCell>
            ))}

            {showVolumeMethod ? <TableCell>{getNLegPreviewVolumeMethod(value.volumeType)}</TableCell> : null}
            {costColumns ? costColumns.map(column => <TableCell key={column.title}>{column.render(value)}</TableCell>)
              : <TableCell>{addSeperatorToInput(value.maxPairPosition, "negativeDecimalNumbers") || "-"}</TableCell>}
            <TableCell>{value.sendMode === NLegSendMode.Sequential ? "ترتیبی" : "هم‌زمان"}</TableCell>
            <TableCell>
              <PrioritySequence value={value.sendMode === NLegSendMode.Sequential
                ? value.sendPriorities : undefined} />
            </TableCell>

            {onRemoveRow ? (
              <TableCell className="!w-12">
                <IconButton
                  size="small"
                  onClick={() => onRemoveRow(value.userId!)}
                  aria-label="remove row"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            ) : null}
          </TableRow>
        ))
      ) : (
        <TableRow className="!relative h-10 !w-full">
          <TableCell className="!w-full !m-auto !flex !justify-center !items-center !h-full !absolute">
            موردی یافت نشد
          </TableCell>
        </TableRow>
      )}
    </>
  );

  return <CompactOrderPreviewTable header={getHeader()} body={getBody()} />;
};

export default NLegOrderPreviewTable;
